import React from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import "firebase/compat/storage";
import "firebase/compat/firestore";

import { getRecoil } from "recoil-nexus";
import Bugsnag from "@bugsnag/js";
import _ from "underscore";

import organizationIdSelector from "../atoms/organizationIdSelector";
import { nanoid } from "nanoid";
import { consola } from "consola";
import { useAdminDraftOptional } from "../context/AdminDraftContext";

export type LabelData = {
  id?: string;
  name: string;
  color: string;
  backgroundColor: string;
  createdOn: Date;
  updatedOn: Date;
  index: number;
};

type UseLabelsOpts = {
  adminDraftMode?: boolean;
};

const useLabels = (opts: UseLabelsOpts = {}) => {
  const { adminDraftMode = false } = opts;
  const organizationId = getRecoil(organizationIdSelector);
  const adminDraft = useAdminDraftOptional();
  const routeAdmin = Boolean(adminDraftMode && adminDraft);

  const [liveLabels, setLiveLabels] = React.useState<LabelData[]>();
  const [loading, setLoading] = React.useState(true);

  const labels = React.useMemo(() => {
    if (!routeAdmin || !adminDraft) return liveLabels;
    const wd = adminDraft.workingDraft;
    if (wd?.labels !== undefined) {
      return _.sortBy(wd.labels, "index");
    }
    return liveLabels;
  }, [routeAdmin, adminDraft, liveLabels]);
  
  const orderLabels = React.useCallback(
    async function (newLabels: any) {
      if (routeAdmin && adminDraft) {
        const reindexed = newLabels.map((e: LabelData, i: number) => ({
          ...e,
          index: i,
        }));
        adminDraft.updateLabels(reindexed);
        return;
      }
      consola.debug("++++++ BATCH -> useLabels: orderLabels")
      const batch = firebase.firestore().batch();
      let index = 0;
      newLabels.forEach((e: { id: string | undefined; }) => {
        const splitRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("labels")
          .doc(e.id);
        consola.debug("+++++ WRITE => useLabels: orderLabels")
        batch.set(
          splitRef,
          {
            index: index,
          },
          { merge: true },
        );
        index += 1;
      });
    consola.debug("+++++ WRITE => useLabels: orderLabels")
    await batch.commit();    },
    [organizationId, routeAdmin, adminDraft],
  );
  const editLabelData = React.useCallback(
    async function (labelData: LabelData) {
      if (routeAdmin && adminDraft) {
        let id = nanoid();
        if (labelData.id) {
          id = labelData.id;
        }
        const next = _.clone(labels || []);
        const idx = _.findIndex(next, { id });
        const row = { ...labelData, id };
        if (idx === -1) {
          next.push({
            ...row,
            index: row.index ?? next.length,
          } as LabelData);
        } else {
          next[idx] = row as LabelData;
        }
        adminDraft.updateLabels(next);
        return;
      }
      // Add the log data here.
      let id = nanoid();
      if (labelData.id) {
        id = labelData.id;
      }
      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("labels")
        .doc(id);

      try {
        consola.debug("+++++ WRITE => useLabels: editLabelData")
        await ref.set(
          {
            id: id,
            name: labelData.name,
            color: labelData.color,
            backgroundColor: labelData.backgroundColor,
            createdOn: labelData.createdOn,
            updatedOn: labelData.updatedOn,
            index: labelData.index,
          },
          { merge: true },
        );
      } catch (e) {
        Bugsnag.notify(new Error("Error saving label data"));
      }
    },
    [organizationId, routeAdmin, adminDraft, labels],
  );

  // TODO: Deleting a label should activate a cloud function to remove references.
  const deleteLabelData = React.useCallback(
    async function (id: string) {
      if (routeAdmin && adminDraft) {
        const next = _.clone(labels || []).filter((l) => l.id !== id);
        adminDraft.updateLabels(next);
        return;
      }
      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("labels")
        .doc(id);

      try {
        const protocolRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("protocols")
          .where("protocolLabels", "array-contains-any", [id]);

        consola.debug("++++++ READ -> useLabels: deleteLabelData")
        const protocols = await protocolRef.get();

        const promises = protocols.docs.map((protocol) => {
          const data = protocol.data();
          if (data.protocolLabels) {
            const labels = _.clone(data.protocolLabels);
            const index = labels.indexOf(id);
            if (index !== -1) {
              labels.splice(index, 1);
              let newIndex = 0;
              labels.forEach((item: { index: number; }) => {
                item.index = newIndex;
                newIndex +=1})
              consola.debug("+++++ WRITE => useLabels: deleteLabelData")
              return protocol.ref.set(
                {
                  protocolLabels: labels,
                },
                { merge: true },
              );
            }
          }
          return null;
        });
        await Promise.all(promises);

        await ref.delete();
      } catch (e) {
        Bugsnag.notify(e as Error);
      }
    },
    [organizationId, routeAdmin, adminDraft, labels],
  );

  const getLabelById = React.useCallback(
    (id: string) => {
      if (!labels) {
        return null;
      }
      const value = _.findWhere(labels, { id: id });
      return value;
    },
    [labels],
  );

  const moveLabel = React.useCallback(
    async (id: any, direction: any) => {
      if (!labels) {
        return;
      }
      if (direction === "up") {
        const index = _.findIndex(labels, { id });

        if (index !== -1) {
          if (index > 0) {
            // get prev index label
            const prevLabel = labels[index - 1];
            const currLabel = labels[index];

            // Swap indexes
            const prevLabelIndex = prevLabel.index;
            prevLabel.index = currLabel.index;
            currLabel.index = prevLabelIndex;

            // Save them
            await editLabelData(currLabel as LabelData);
            await editLabelData(prevLabel as LabelData);
          }
        }
      } else {
        const index = _.findIndex(labels, { id });
        if (index !== -1) {
          if (index < labels.length - 1) {
            // get prev index label
            const nextLabel = labels[index + 1];
            const currLabel = labels[index];

            // Swap indexes
            const nextLabelIndex = nextLabel.index;
            nextLabel.index = currLabel.index;
            currLabel.index = nextLabelIndex;

            // Save them
            await editLabelData(currLabel as LabelData);
            await editLabelData(nextLabel as LabelData);
          }
        }
      }
    },
    [labels, editLabelData],
  );

  React.useEffect(() => {
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("labels")
      .orderBy("index");

    const subscription = ref.onSnapshot((snapshot) => {
      consola.debug("++++++ SNAPSHOT -> useLabels")
      const data: Array<LabelData> = snapshot.docs.map((d) => {
        return d.data() as LabelData;
      });
      setLoading(false);
      if (data.length === 0) {
        setLiveLabels([]);
      } else {
        setLiveLabels(data);
      }
    }, (error) => {
      consola.error("useLabels snapshot error:", error);
      setLoading(false);
      setLiveLabels([]);
    });

    return () => {
      subscription();
    };
  }, [organizationId]);

  return { labels, loading, editLabelData, deleteLabelData, moveLabel, getLabelById, orderLabels };
};

export default useLabels;
